/**
 * FILA dataLayer 헬퍼 유틸리티
 * GTM 컨테이너(GTM-PH32ZNKV)와 함께 사용
 *
 * @typedef {Object} DataLayerItem
 * @property {string}  item_id          - 스타일코드 (예: "1100FS261FT01X001496")
 * @property {string}  item_name        - 상품명
 * @property {number}  price            - 상품 가격
 * @property {string}  item_brand       - "FILA" | "KIDS" | "INTIMO"
 * @property {string}  item_category    - 성별 카테고리 (공용, KIDS, Women, Men)
 * @property {string}  item_category2   - 대분류 (의류, 신발, 가방 등)
 * @property {string}  [item_category3] - 소분류
 * @property {string}  [item_variant]   - 색상 옵션
 * @property {string}  [item_variant2]  - 사이즈 옵션
 * @property {number}  [quantity]       - 수량
 * @property {number}  [index]          - 리스트 내 순서
 */

window.FilaDL = (function () {
  'use strict';

  // item_id 정규화 대상: 영숫자로만 이루어진 코드값(oms_cd, styl_cd, 코드형 handle)
  // gid://shopify/... 처럼 구분자가 섞인 fallback 값은 대문자화 시 식별자가 훼손되고
  // 제품 피드와도 매칭되지 않으므로 원본 유지
  var ALNUM_CODE_RE = /^[A-Za-z0-9]+$/;

  function shallowCopy(obj) {
    var copy = {};
    for (var key in obj) {
      if (Object.prototype.hasOwnProperty.call(obj, key)) copy[key] = obj[key];
    }
    return copy;
  }

  /**
   * ecommerce.items[].item_id를 대문자로 정규화 (원본 객체는 변형하지 않음)
   * 제품 피드가 대문자 형식이므로 GA4에서 같은 상품이 분리 집계되는 것을 방지
   * @param {Object} data
   * @returns {Object} 정규화가 필요 없으면 원본, 필요하면 복사본
   */
  function normalizeItemIds(data) {
    if (!data || typeof data !== 'object') return data;
    var ecommerce = data.ecommerce;
    if (!ecommerce || typeof ecommerce !== 'object') return data;
    var items = ecommerce.items;
    if (!items || !items.length) return data;

    var changed = false;
    var nextItems = [];
    for (var i = 0; i < items.length; i++) {
      var item = items[i];
      if (!item || typeof item !== 'object' || typeof item.item_id !== 'string' || !ALNUM_CODE_RE.test(item.item_id)) {
        nextItems.push(item);
        continue;
      }
      var upper = item.item_id.toUpperCase();
      if (upper === item.item_id) {
        nextItems.push(item);
        continue;
      }
      var itemCopy = shallowCopy(item);
      itemCopy.item_id = upper;
      nextItems.push(itemCopy);
      changed = true;
    }
    if (!changed) return data;

    var dataCopy = shallowCopy(data);
    dataCopy.ecommerce = shallowCopy(ecommerce);
    dataCopy.ecommerce.items = nextItems;
    return dataCopy;
  }

  /**
   * 기본 dataLayer push
   * @param {Object} data - push할 데이터
   */
  function push(data) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(normalizeItemIds(data));
  }

  /**
   * ecommerce 이벤트 push (이전 ecommerce 데이터 초기화 후 push)
   * @param {Object} data - ecommerce 포함 이벤트 데이터
   */
  function pushEcommerce(data) {
    push({ ecommerce: null });
    push(data);
  }

  /**
   * 디바이스 타입 반환
   * @returns {"mobile"|"desktop"}
   */
  function getDeviceType() {
    var ua = navigator.userAgent;
    if (/Android|iPhone|iPod|webOS|BlackBerry|IEMobile|Opera Mini/i.test(ua)) return 'mobile';
    if (/iPad/i.test(ua)) return 'mobile';
    if (/Macintosh/i.test(ua) && navigator.maxTouchPoints > 1) return 'mobile';
    return 'desktop';
  }

  /**
   * 디바이스 약어 반환 (gnb_menu용)
   * @returns {"mo"|"pc"}
   */
  function getDeviceShort() {
    return getDeviceType() === 'mobile' ? 'mo' : 'pc';
  }

  /**
   * 현재 페이지의 main page_type 반환
   * @returns {string}
   */
  function getMainPageType() {
    var path = window.location.pathname;
    if (path === '/') return 'main';
    if (path === '/pages/kids' || path === '/pages/kids-main') return 'kids_main';
    if (path === '/pages/underwear' || path === '/pages/underwear-main') return 'underwear_main';
    return 'main';
  }

  /**
   * Liquid에서 출력된 product JSON으로 DataLayerItem 구성
   * @param {Object} productData - Liquid에서 생성된 상품 데이터
   * @param {number} [index] - 리스트 내 순서
   * @returns {DataLayerItem}
   */
  function buildItem(productData, index) {
    var item = {
      item_id: productData.item_id || productData.handle || '',
      item_name: productData.item_name || '',
      price: typeof productData.price === 'number' ? productData.price : 0,
      item_brand: productData.item_brand || 'FILA',
      item_category: productData.item_category || '',
      item_category2: productData.item_category2 || ''
    };
    if (productData.item_category3) item.item_category3 = productData.item_category3;
    if (productData.item_variant) item.item_variant = productData.item_variant;
    if (productData.item_variant2) item.item_variant2 = productData.item_variant2;
    if (typeof productData.quantity === 'number') item.quantity = productData.quantity;
    if (typeof index === 'number') item.index = index;
    return item;
  }

  /**
   * HTML 태그 제거 (richtext에서 순수 텍스트 추출)
   * @param {string} html
   * @returns {string}
   */
  function stripHTML(html) {
    if (!html) return '';
    var tmp = document.createElement('div');
    tmp.innerHTML = html;
    return (tmp.textContent || tmp.innerText || '').trim();
  }

  /**
   * 태그 배열에서 성별/대분류/소분류 카테고리 추출
   * datalayer-product-data.liquid 로직과 동일
   * @param {string[]} tags
   * @returns {{ item_category: string, item_category2: string, item_category3: string }}
   */
  function buildItemFromTags(tags) {
    if (!tags || !tags.length) return { item_category: '', item_category2: '', item_category3: '' };

    var hasGongyong = false, hasYeoseong = false, hasNamseong = false, hasNama = false, hasYeoa = false;
    var cat2 = '', cat3 = '', gender = '';
    var cat2Map = { '의류':1, '신발':1, '가방':1, '용품':1, '언더웨어':1, '드로즈':1, '모자':1, '양말':1 };
    var skipTags = { '공용':1, '여성':1, '남성':1, '남아':1, '여아':1, '키즈':1, '의류':1, '신발':1, '가방':1, '용품':1, '언더웨어':1, '드로즈':1, '모자':1, '양말':1, '사원판매':1, 'STANDARD 핏':1, '라이프스타일':1, '테니스':1, '아웃도어':1 };

    for (var i = 0; i < tags.length; i++) {
      var tag = tags[i];
      if (tag === '공용') hasGongyong = true;
      else if (tag === '여성') hasYeoseong = true;
      else if (tag === '남성') hasNamseong = true;
      else if (tag === '남아') hasNama = true;
      else if (tag === '여아') hasYeoa = true;
      if (!cat2 && cat2Map[tag]) cat2 = tag;
      if (!cat3 && !skipTags[tag] && tag.split('/')[0] !== '단체구매') cat3 = tag;
    }

    if (hasGongyong) gender = '공용';
    else if (hasNama || hasYeoa) gender = 'KIDS';
    else if (hasYeoseong) gender = 'Women';
    else if (hasNamseong) gender = 'Men';

    return { item_category: gender, item_category2: cat2, item_category3: cat3 };
  }

  /**
   * 상품의 브랜드(item_brand) 추출 — datalayer-product-brand.liquid와 동일 로직
   * 우선순위: brnd_cd 메타필드 → tags fallback → 'FILA'
   * @param {string|null|undefined} brandCode - product.metafields.custom.brnd_cd ('1100'|'1170'|'1200')
   * @param {string[]} [tags] - product.tags
   * @returns {"FILA"|"KIDS"|"INTIMO"}
   */
  function deriveBrand(brandCode, tags) {
    if (brandCode === '1170') return 'INTIMO';
    if (brandCode === '1200') return 'KIDS';
    if (brandCode === '1100') return 'FILA';
    if (tags && tags.length) {
      var hasIntimo = false, hasKids = false;
      for (var i = 0; i < tags.length; i++) {
        var t = tags[i];
        if (t === '언더웨어' || t === '드로즈') hasIntimo = true;
        else if (t === '남아' || t === '여아' || t === '키즈') hasKids = true;
      }
      if (hasIntimo) return 'INTIMO';
      if (hasKids) return 'KIDS';
    }
    return 'FILA';
  }

  return {
    push: push,
    pushEcommerce: pushEcommerce,
    getDeviceType: getDeviceType,
    getDeviceShort: getDeviceShort,
    getMainPageType: getMainPageType,
    buildItem: buildItem,
    buildItemFromTags: buildItemFromTags,
    deriveBrand: deriveBrand,
    stripHTML: stripHTML
  };
})();

// PLP용 restock_request 글로벌 리스너 (PDP에서는 main-product.liquid 리스너가 처리)
document.addEventListener('click', function(e) {
  // PDP에서는 main-product.liquid 리스너가 처리하므로 스킵
  if (document.querySelector('[data-dl-view-item]')) return;

  var restockBtn = e.target.closest('.restock-button, .card__content-option-item--unavailable');
  if (!restockBtn) return;
  if (typeof FilaDL === 'undefined') return;

  var card = e.target.closest('[data-dl-product-card]');
  if (!card) return;

  var size = restockBtn.getAttribute('data-option-value')
    || restockBtn.getAttribute('data-size-value')
    || '';

  FilaDL.pushEcommerce({
    event: 'restock_request',
    size: size,
    ecommerce: {
      items: [{
        item_id: card.getAttribute('data-dl-item-id') || '',
        item_name: card.getAttribute('data-dl-item-name') || '',
        price: parseFloat(card.getAttribute('data-dl-item-price')) || 0,
        item_brand: card.getAttribute('data-dl-item-brand') || 'FILA'
      }]
    }
  });
});
